export const C = {
  // Core Palette
  navy:        "#0F172A",
  navyMid:     "#1E293B",
  navyLight:   "#334155",
  blue:        "#1B2A4A",
  blueMid:     "#1B3A6B",
  blueLight:   "#2D5494",
  bluePale:    "#DBEAFE",
  gold:        "#C9A84C",
  goldLight:   "#FEF3C7",
  goldDark:    "#A8832A",
  emerald:     "#10B981",
  emeraldLight:"#D1FAE5",
  rose:        "#F43F5E",
  roseLight:   "#FFE4E6",
  purple:      "#8B5CF6",
  purpleLight: "#EDE9FE",
  slate:       "#64748B",
  slateLight:  "#F1F5F9",
  white:       "#FFFFFF",
  glass:       "rgba(255,255,255,0.07)",
  glassBorder: "rgba(255,255,255,0.12)",
  glassDark:   "rgba(0,0,0,0.2)",
};

export const G = {
  navy:    `linear-gradient(135deg, #0F172A 0%, #1E293B 100%)`,
  blue:    `linear-gradient(135deg, #1B2A4A 0%, #2D5494 100%)`,
  gold:    `linear-gradient(135deg, #A8832A 0%, #C9A84C 100%)`,
  emerald: `linear-gradient(135deg, #059669 0%, #10B981 100%)`,
  rose:    `linear-gradient(135deg, #E11D48 0%, #F43F5E 100%)`,
  purple:  `linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)`,
  hero:    `linear-gradient(135deg, #0A1628 0%, #1B2A4A 50%, #1B3A6B 100%)`,
  sidebar: `linear-gradient(180deg, #0A1628 0%, #1B2A4A 100%)`,
  card:    `linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)`,
};

// Animation keyframes injected once
export const ANIMATIONS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'Inter', sans-serif; background: #0A1628; color: #F8FAFC; }
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: #1E293B; }
::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideIn { from { opacity: 0; transform: translateX(-16px); } to { opacity: 1; transform: translateX(0); } }
@keyframes pulse { 0%,100% { opacity: 0.4; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.15); } }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes bounceIn { 0% { transform: scale(0.3); opacity: 0; } 50% { transform: scale(1.05); } 70% { transform: scale(0.9); } 100% { transform: scale(1); opacity: 1; } }
@keyframes glow { 0%,100% { box-shadow: 0 0 10px rgba(37,99,235,0.3); } 50% { box-shadow: 0 0 25px rgba(37,99,235,0.7); } }
@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
.fade-in { animation: fadeIn 0.4s ease forwards; }
.slide-in { animation: slideIn 0.3s ease forwards; }
.bounce-in { animation: bounceIn 0.5s cubic-bezier(0.36,0.07,0.19,0.97) forwards; }
`;

export const S = {
  // Layout
  appWrap: { minHeight: "100vh", background: C.navy, fontFamily: "'Inter',sans-serif", color: C.white },
  layout: { display: "flex", minHeight: "100vh" },
  sidebar: {
    width: 270, background: G.sidebar, display: "flex", flexDirection: "column",
    position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100,
    borderRight: `1px solid ${C.glassBorder}`, overflowY: "auto",
  },
  mainArea: { marginLeft: 270, flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", background: "#0B1120" },
  topbar: {
    background: "rgba(15,23,42,0.95)", backdropFilter: "blur(20px)",
    borderBottom: `1px solid ${C.glassBorder}`, padding: "14px 32px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    position: "sticky", top: 0, zIndex: 50,
  },
  content: { flex: 1, padding: "28px 32px", overflowY: "auto" },

  // Glass Cards
  glassCard: {
    background: "rgba(255,255,255,0.04)", backdropFilter: "blur(20px)",
    border: `1px solid rgba(255,255,255,0.08)`, borderRadius: 16,
    transition: "all 0.3s ease",
  },
  card: {
    background: "rgba(255,255,255,0.04)", border: `1px solid rgba(255,255,255,0.08)`,
    borderRadius: 16, overflow: "hidden",
  },
  cardHover: {
    background: "rgba(255,255,255,0.07)", border: `1px solid rgba(255,255,255,0.15)`,
    borderRadius: 16, overflow: "hidden", cursor: "pointer",
    transition: "all 0.25s ease", transform: "translateY(0)",
  },

  // Inputs
  formGroup: { marginBottom: 20 },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "#94A3B8", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.5 },
  input: {
    width: "100%", padding: "12px 16px", borderRadius: 10,
    border: `1.5px solid rgba(255,255,255,0.1)`,
    background: "#1E293B", color: C.white,
    fontSize: 14, outline: "none", transition: "border 0.2s",
    fontFamily: "inherit", boxSizing: "border-box",
    colorScheme: "dark",
  },

  // Buttons
  btn: (v = "primary", sz = "md") => {
    const variants = {
      primary: { bg: G.blue, color: C.white, border: "none", shadow: "0 4px 15px rgba(37,99,235,0.35)" },
      gold:    { bg: G.gold, color: C.navy, border: "none", shadow: "0 4px 15px rgba(245,158,11,0.35)" },
      success: { bg: G.emerald, color: C.white, border: "none", shadow: "0 4px 15px rgba(16,185,129,0.3)" },
      danger:  { bg: G.rose, color: C.white, border: "none", shadow: "0 4px 15px rgba(244,63,94,0.3)" },
      purple:  { bg: G.purple, color: C.white, border: "none", shadow: "0 4px 15px rgba(139,92,246,0.3)" },
      ghost:   { bg: "transparent", color: "#94A3B8", border: "none", shadow: "none" },
      outline: { bg: "transparent", color: C.blueLight, border: `1.5px solid ${C.blueLight}`, shadow: "none" },
      glass:   { bg: "rgba(255,255,255,0.07)", color: C.white, border: `1px solid rgba(255,255,255,0.12)`, shadow: "none" },
    };
    const sizes = {
      sm: { padding: "6px 14px", fontSize: 12, borderRadius: 8 },
      md: { padding: "10px 20px", fontSize: 14, borderRadius: 10 },
      lg: { padding: "13px 28px", fontSize: 15, borderRadius: 12 },
    };
    const cfg = variants[v] || variants.primary;
    const s = sizes[sz] || sizes.md;
    return {
      background: cfg.bg, color: cfg.color, border: cfg.border || "none",
      boxShadow: cfg.shadow, borderRadius: s.borderRadius, padding: s.padding,
      fontSize: s.fontSize, fontWeight: 600, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 7,
      transition: "all 0.2s ease", whiteSpace: "nowrap", fontFamily: "inherit",
    };
  },

  // Badges
  badge: (type) => {
    const map = {
      paid:      ["rgba(16,185,129,0.15)",  "#10B981", "rgba(16,185,129,0.3)"],
      partial:   ["rgba(245,158,11,0.15)",  "#F59E0B", "rgba(245,158,11,0.3)"],
      unpaid:    ["rgba(244,63,94,0.15)",   "#F43F5E", "rgba(244,63,94,0.3)"],
      success:   ["rgba(16,185,129,0.15)",  "#10B981", "rgba(16,185,129,0.3)"],
      warning:   ["rgba(245,158,11,0.15)",  "#F59E0B", "rgba(245,158,11,0.3)"],
      danger:    ["rgba(244,63,94,0.15)",   "#F43F5E", "rgba(244,63,94,0.3)"],
      info:      ["rgba(59,130,246,0.15)",  "#3B82F6", "rgba(59,130,246,0.3)"],
      purple:    ["rgba(139,92,246,0.15)",  "#8B5CF6", "rgba(139,92,246,0.3)"],
      gold:      ["rgba(245,158,11,0.15)",  "#F59E0B", "rgba(245,158,11,0.3)"],
      gray:      ["rgba(100,116,139,0.15)", "#64748B", "rgba(100,116,139,0.3)"],
      locked:    ["rgba(100,116,139,0.15)", "#64748B", "rgba(100,116,139,0.3)"],
      general:   ["rgba(59,130,246,0.15)",  "#3B82F6", "rgba(59,130,246,0.3)"],
      core:      ["rgba(139,92,246,0.15)",  "#8B5CF6", "rgba(139,92,246,0.3)"],
      specialist:["rgba(245,158,11,0.15)",  "#F59E0B", "rgba(245,158,11,0.3)"],
    };
    const [bg, col, border] = map[type] || map.gray;
    return { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: bg, color: col, border: `1px solid ${border}` };
  },

  // Modal
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal: (w = 560) => ({
    background: "linear-gradient(135deg, #1E293B, #0F172A)",
    border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 20,
    width: "100%", maxWidth: w, maxHeight: "90vh", overflow: "auto",
    boxShadow: "0 30px 100px rgba(0,0,0,0.5)",
  }),
  modalHead: { padding: "22px 28px", borderBottom: `1px solid rgba(255,255,255,0.08)`, display: "flex", alignItems: "center", justifyContent: "space-between" },
  modalBody: { padding: 28 },
  modalFoot: { padding: "16px 28px", borderTop: `1px solid rgba(255,255,255,0.06)`, display: "flex", gap: 12, justifyContent: "flex-end" },

  // Table
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "11px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748B", background: "rgba(255,255,255,0.03)", borderBottom: `1px solid rgba(255,255,255,0.06)`, textTransform: "uppercase", letterSpacing: 0.8 },
  td: { padding: "13px 16px", fontSize: 14, color: "#CBD5E1", borderBottom: `1px solid rgba(255,255,255,0.04)` },

  // Progress
  progressWrap: { height: 8, borderRadius: 4, background: "rgba(255,255,255,0.08)", overflow: "hidden" },
  progressFill: (pct, color = C.blueMid) => ({ height: "100%", width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4, transition: "width 0.6s ease" }),

  // Auth
  authWrap: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: G.hero, padding: 20, fontFamily: "'Inter',sans-serif" },
  authCard: { background: "rgba(255,255,255,0.05)", backdropFilter: "blur(30px)", border: `1px solid rgba(255,255,255,0.12)`, borderRadius: 24, padding: "48px 44px", width: "100%", maxWidth: 460, boxShadow: "0 30px 80px rgba(0,0,0,0.4)" },

  // Misc
  pageTitle: { fontSize: 24, fontWeight: 800, color: C.white, margin: 0 },
  pageSub: { fontSize: 14, color: "#64748B", marginTop: 4 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 18, marginBottom: 28 },
  emptyWrap: { textAlign: "center", padding: "56px 24px", color: "#475569" },
  divider: { height: 1, background: "rgba(255,255,255,0.06)", margin: "20px 0" },
  tabRow: { display: "flex", gap: 4, borderBottom: `1px solid rgba(255,255,255,0.08)`, marginBottom: 24 },
  tab: (a) => ({ padding: "10px 22px", fontSize: 14, fontWeight: 600, border: "none", background: "none", cursor: "pointer", color: a ? C.blueLight : "#64748B", borderBottom: a ? `2px solid ${C.blueLight}` : "2px solid transparent", marginBottom: -1, transition: "all 0.2s", fontFamily: "inherit" }),
  alert: (t) => {
    const m = { info: ["rgba(59,130,246,0.12)", "#3B82F6"], success: ["rgba(16,185,129,0.12)", "#10B981"], warning: ["rgba(245,158,11,0.12)", "#F59E0B"], danger: ["rgba(244,63,94,0.12)", "#F43F5E"] };
    const [bg, col] = m[t] || m.info;
    return { padding: "12px 16px", borderRadius: 10, fontSize: 13, background: bg, color: col, border: `1px solid ${col}33`, marginBottom: 16 };
  },
};

export const uid = () => Math.random().toString(36).slice(2, 9);
export const today = () => new Date().toISOString().split("T")[0];
export const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" }) : "—";
export const isPast = (d) => d && new Date(d) < new Date();
export const calcGrade = (p) => !p ? "—" : p >= 90 ? "A+" : p >= 80 ? "A" : p >= 70 ? "B" : p >= 60 ? "C" : "F";
export const genStudentId = (year) => `UNIV-${year || new Date().getFullYear()}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, "0")}`;
